import React from 'react';
import HomeLink from './HomeLink';
import { CookieSettingsLink, DoNotSellOrShareLink } from '../ConsentBanner';
import { CONTENT_SHELL, FOCUS_RING_ON_BRAND } from './tokens';

/**
 * HOME-01 — the Home footer, in the design's brand-green treatment.
 *
 * Scoped to Home. `src/components/FooterSSR.tsx` is untouched and still serves
 * every other page — a footer swap across ~20 surfaces is not part of a Home
 * redesign, and doing it here would put the rollback flag in charge of pages it
 * has no business over.
 *
 * EVERY LINK RESOLVES. The design names twelve; four of them have no page in
 * this product and are omitted rather than pointed somewhere plausible:
 *
 *   Areas        — there is no /areas route. Areas are reached by searching,
 *                  which is what the "Browse by area" tiles already do.
 *   Verification — described only inside the agent dashboard's own modal; no
 *                  public page states the policy.
 *   Cookies      — no cookie policy PAGE exists, and none was invented. GA4-02
 *                  adds a "Cookie settings" control to the Legal column
 *                  instead: it reopens the consent banner, which is a thing
 *                  this build actually has, rather than linking to a document
 *                  it does not.
 *   Store badges — the apps are live, but no App Store or Play URL appears
 *                  anywhere in this repo. A store badge that is not a link is
 *                  furniture pretending to be a button, and a guessed store URL
 *                  is worse. Both were refused.
 *
 * All four are reported to Dirie. Every remaining destination is one the
 * existing footer already ships, so nothing here is a new claim about what this
 * site contains.
 *
 * `external` on the guides link: /guides is Vike-rendered and has no route in
 * App.tsx, so the SPA has to leave the app to reach it (REL-03).
 */

interface FooterColumn {
  title: string;
  links: Array<{ label: string; href: string; external?: boolean }>;
}

const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    title: 'Buy',
    links: [
      { label: 'Search homes', href: '/' },
      { label: 'Guides', href: '/guides', external: true },
    ],
  },
  {
    title: 'For agents',
    links: [
      // /pricing carries the plan table and the Spotlight Boost section, so
      // both labels land on something that actually describes them.
      { label: 'Plans & pricing', href: '/pricing', external: true },
      { label: 'Spotlight', href: '/pricing', external: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', href: '/about', external: true },
      { label: 'Careers', href: '/careers', external: true },
      { label: 'Contact', href: '/contact', external: true },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms', href: '/terms' },
      { label: 'Privacy', href: '/privacy' },
      // SYND-01's policy page. It belongs with the rules, not the introductions.
      { label: 'How listings work', href: '/syndication' },
    ],
  },
];

/**
 * The boilerplate, verbatim from the design.
 *
 * Three product truths in one sentence and not a word past them: this is a
 * marketplace (not an agency), buyers and agents talk in-app, and verification
 * is something an agent CAN earn. It does not say all agents are verified, does
 * not say listings are checked, and does not mention renting.
 */
const FOOTER_BOILERPLATE =
  'Afriqahome is a marketplace for buying and selling property in Kenya. Buyers and agents message in-app, and agents can earn a verified badge.';

const HomeFooter: React.FC = () => (
  // The footer background is full-bleed; its CONTENT sits in the same centred,
  // capped shell as every row above it (HOME-01 fix round 1), so the first
  // column lines up with the page rather than with the monitor.
  <footer className="bg-[#134025] pb-9 pt-10 text-[#C9D3CB] lg:pt-12">
    <div className={CONTENT_SHELL}>
    <div className="grid grid-cols-2 gap-8 lg:grid-cols-[repeat(4,1fr)_1.2fr]">
      {FOOTER_COLUMNS.map((column) => (
        <div key={column.title}>
          <div className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white">
            {column.title}
          </div>
          <div className="flex flex-col gap-2.5 text-[13.5px]">
            {column.links.map((link) => (
              <HomeLink
                key={`${column.title}:${link.label}`}
                to={link.href}
                external={link.external}
                className={`rounded text-[#C9D3CB] hover:text-white hover:underline ${FOCUS_RING_ON_BRAND}`}
              >
                {link.label}
              </HomeLink>
            ))}
            {/* GA4-02, then GA4-14 — see the "Cookies" note in the header
                comment. Buttons, not links: they open the per-category settings
                panel rather than navigating. The second is the CPRA-mandated
                opt-out link and opens the panel focused on Advertising. */}
            {column.title === 'Legal' && (
              <>
                <CookieSettingsLink
                  className={`self-start rounded text-left text-[#C9D3CB] hover:text-white hover:underline ${FOCUS_RING_ON_BRAND}`}
                />
                <DoNotSellOrShareLink
                  className={`self-start rounded text-left text-[#C9D3CB] hover:text-white hover:underline ${FOCUS_RING_ON_BRAND}`}
                />
              </>
            )}
          </div>
        </div>
      ))}

      <div className="col-span-2 flex flex-col gap-2.5 lg:col-span-1">
        <img
          src="/afriqahome-logo.svg"
          alt="Afriqahome"
          className="h-7 w-auto self-start brightness-0 invert"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>

    <div className="mt-9 max-w-[640px] border-t border-white/[0.16] pt-5 text-[12.5px] leading-[1.6] text-[#8FA396]">
      {FOOTER_BOILERPLATE}
    </div>
    </div>
  </footer>
);

export default HomeFooter;
